import React, { useState, useEffect } from 'react';
import { customerService, storeService } from '../services/api';
import { GlassPanel, GlassButton, GlassInput, GlassSelect, GlassCard, PageHeader } from '../components/GlassUI';
import { FaUser, FaPhone, FaMapPin, FaCalendar, FaInstagram, FaLink, FaCheck, FaPlus, FaWhatsapp, FaQrcode, FaUserPlus, FaShareAlt } from 'react-icons/fa';
import QrCode from '../components/QrCode';
import { downloadVCard, shareVCard } from '../utils/download';

// Fallback shop details until the owner fills them in (Profile →
// Business / Shop Details). Live values come from GET /api/settings/public.
const DEFAULT_SHOP = {
  companyName: 'Apni Jewellers',
  phone: '',
  whatsapp: '',
  website: '',
  address: '',
};

interface CustomerOnboardingPageProps {
  onNavigate?: (page: string) => void;
}

const PROFESSION_OPTIONS = [
  { value: '', label: 'Select Profession' },
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'LAWYER', label: 'Lawyer' },
  { value: 'TEACHER', label: 'Teacher' },
  { value: 'ENGINEER', label: 'Engineer' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'RETIRED', label: 'Retired' },
  { value: 'HOUSEWIFE', label: 'Housewife' },
  { value: 'STUDENT', label: 'Student' },
  { value: 'OTHER', label: 'Other' },
];

const CHANNEL_OPTIONS = [
  { value: 'ONLINE', label: 'Online' },
  { value: 'OFFLINE_IN_STORE', label: 'Offline - In Store' },
  { value: 'EXHIBITION', label: 'Exhibition' },
];

export const CustomerOnboardingPage: React.FC<CustomerOnboardingPageProps> = ({ onNavigate }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<any>(null);
  const [error, setError] = useState('');
  const [shop, setShop] = useState({ ...DEFAULT_SHOP });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await storeService.getPublic();
        const s = res.data?.store;
        if (!cancelled && s) setShop((prev) => ({ ...prev, ...s }));
      } catch {
        // Keep the defaults if the shop has not configured details yet.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getUrlSource = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('source') || '';
  };

  const urlSource = getUrlSource();

  const [form, setForm] = useState({
    name: '',
    cellNumber: '',
    secondaryNumber: '',
    whatsappNumber: '',
    address: '',
    instagramHandle: '',
    anniversaryDate: '',
    dateOfBirth: '',
    channelSource: urlSource || 'OFFLINE_IN_STORE',
    profession: '',
    referenceName: '',
    referencePhone: '',
  });

  useEffect(() => {
    const source = getUrlSource();
    if (source) {
      setForm(prev => ({ ...prev, channelSource: source }));
    }
  }, []);

  const update = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError('Customer name is required');
      return;
    }
    if (!form.cellNumber.trim()) {
      setError('Phone number is required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload: any = {
        cellNumber: form.cellNumber.trim(),
        customerName: form.name.trim(),
      };
      if (form.whatsappNumber) payload.whatsappNumber = form.whatsappNumber;
      if (form.secondaryNumber) payload.secondaryNumber = form.secondaryNumber;
      if (form.address) payload.location = form.address;
      if (form.instagramHandle) payload.socialHandle = form.instagramHandle;
      if (form.anniversaryDate) payload.anniversary = form.anniversaryDate;
      if (form.dateOfBirth) payload.dateOfBirth = form.dateOfBirth;
      if (form.channelSource) payload.channelSource = form.channelSource;
      if (form.profession) payload.profession = form.profession;
      if (form.referenceName) payload.reference1Name = form.referenceName;
      if (form.referencePhone) payload.reference1Phone = form.referencePhone;

      const res = await customerService.createOrUpdate(payload);
      setSuccess({
        id: res.data.customer?.id,
        name: form.name,
        phone: form.cellNumber,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      cellNumber: '',
      secondaryNumber: '',
      whatsappNumber: '',
      address: '',
      instagramHandle: '',
      anniversaryDate: '',
      dateOfBirth: '',
      channelSource: urlSource || 'OFFLINE_IN_STORE',
      profession: '',
      referenceName: '',
      referencePhone: '',
    });
    setSuccess(null);
    setError('');
  };

  const storeUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/customer-form?source=QR_SCAN`;

  const shopName = shop.companyName || DEFAULT_SHOP.companyName;
  const shopPhone = shop.phone || '';
  const shopWhatsapp = shop.whatsapp || shop.phone || '';

  const vCardData = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${shopName}`,
    `ORG:${shopName}`,
    shopPhone ? `TEL;TYPE=WORK,VOICE:${shopPhone}` : '',
    shopWhatsapp ? `TEL;TYPE=WHATSAPP:${shopWhatsapp}` : '',
    shop.website ? `URL:${shop.website}` : '',
    shop.address ? `ADR:;;${shop.address};;;;` : '',
    'END:VCARD',
  ].filter(Boolean).join('\n');

  const whatsappLink = `https://wa.me/${shopWhatsapp.replace(/[^0-9]/g, '')}`;

  if (success) {
    return (
      <div className="max-w-lg mx-auto">
        <GlassPanel className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaCheck className="text-green-600 text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Customer Added!</h2>
          <p className="text-slate-500 mb-6">Customer details have been saved successfully.</p>

          <GlassCard className="mb-6 bg-green-50 border-green-200 text-left">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Name</span>
                <span className="font-semibold text-slate-900">{success.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Phone</span>
                <span className="font-semibold text-slate-900">{success.phone}</span>
              </div>
            </div>
          </GlassCard>

          {/* Company Contact QR Code */}
          {(shopPhone || shopWhatsapp) && (
            <GlassCard className="mb-6 bg-blue-50 border-blue-200">
              <div className="flex items-center gap-2 justify-center mb-3">
                <FaQrcode className="text-blue-600 text-xl" />
                <h3 className="font-bold text-slate-900">Save Our Contact</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Save our number to this phone, share it, or scan the QR.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <GlassButton
                  variant="primary"
                  className="flex-1"
                  onClick={() => downloadVCard(`${shopName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-contact`, vCardData)}
                >
                  <FaPhone className="mr-2" /> Save Contact
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  className="flex-1"
                  onClick={() => shareVCard(`${shopName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-contact`, vCardData, `${shopName} contact`, shop.website || undefined)}
                >
                  <FaShareAlt className="mr-2" /> Share
                </GlassButton>
              </div>
              <div className="flex justify-center mb-4">
                <QrCode
                  value={vCardData}
                  size={192}
                  fileName={`${shopName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-contact-qr`}
                  showShare
                  shareTitle={`Save ${shopName} as a contact`}
                />
              </div>
              <div className="space-y-2 text-sm text-slate-700">
                {shopPhone && <p><strong>Phone:</strong> {shopPhone}</p>}
                {shop.whatsapp && <p><strong>WhatsApp:</strong> {shop.whatsapp}</p>}
                {shop.website && <p><strong>Website:</strong> {shop.website}</p>}
              </div>
              {shopWhatsapp && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all"
                >
                  <FaWhatsapp size={20} /> Chat on WhatsApp
                </a>
              )}
            </GlassCard>
          )}

          <div className="flex flex-col gap-3">
            <GlassButton
              variant="primary"
              className="w-full"
              onClick={() => onNavigate?.('customers')}
            >
              View Customers
            </GlassButton>
            <GlassButton variant="secondary" className="w-full" onClick={resetForm}>
              <FaPlus className="mr-2" /> Add Another
            </GlassButton>
            <GlassButton variant="secondary" className="w-full" onClick={() => onNavigate?.('dashboard')}>
              Back to Dashboard
            </GlassButton>
          </div>
        </GlassPanel>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Customer Onboarding"
        subtitle="Add a new customer to the system"
        icon={<FaUserPlus />}
      />

      {/* QR Code Section */}
      <GlassPanel className="p-6 mb-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="text-center md:text-left flex-1">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Customer Self-Registration QR</h3>
            <p className="text-sm text-slate-500 mb-2">
              Print this QR code and place it at your counter. Customers can scan it to fill their own details.
            </p>
            <p className="text-xs text-slate-400 break-all">Source: QR_SCAN</p>
          </div>
          <div className="shrink-0">
            <QrCode
              value={storeUrl}
              size={160}
              fileName="customer-registration-qr"
              downloadLabel="Download QR"
              showShare
              shareTitle={`Register at ${shopName}`}
            />
          </div>
        </div>
      </GlassPanel>

      {/* Onboarding Form */}
      <GlassPanel className="p-6 md:p-8">
        <h2 className="text-xl font-bold text-slate-900 mb-6">Customer Details</h2>

        {error && (
          <GlassCard className="mb-4 bg-red-50 border-red-200">
            <p className="text-red-700 text-sm">{error}</p>
          </GlassCard>
        )}

        <div className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1 text-sm">
              <FaUser className="inline mr-1 text-slate-400" /> Full Name *
            </label>
            <GlassInput
              value={form.name}
              onChange={e => update('name', e.target.value)}
              placeholder="Customer full name"
              required
            />
          </div>

          {/* Primary Phone */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1 text-sm">
              <FaPhone className="inline mr-1 text-slate-400" /> Mobile Number * (Primary ID)
            </label>
            <GlassInput
              type="tel"
              value={form.cellNumber}
              onChange={e => update('cellNumber', e.target.value)}
              placeholder="+91 XXXXX XXXXX"
              required
            />
          </div>

          {/* Secondary Phone & WhatsApp */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-1 text-sm">Secondary Number</label>
              <GlassInput
                type="tel"
                value={form.secondaryNumber}
                onChange={e => update('secondaryNumber', e.target.value)}
                placeholder="Optional secondary phone"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1 text-sm">
                <FaPhone className="inline mr-1 text-green-500" /> WhatsApp Number
              </label>
              <GlassInput
                type="tel"
                value={form.whatsappNumber}
                onChange={e => update('whatsappNumber', e.target.value)}
                placeholder="Same as mobile if blank"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1 text-sm">
              <FaMapPin className="inline mr-1 text-slate-400" /> Address / Location
            </label>
            <GlassInput
              value={form.address}
              onChange={e => update('address', e.target.value)}
              placeholder="City, area, landmark..."
            />
          </div>

          {/* Instagram */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1 text-sm">
              <FaInstagram className="inline mr-1 text-pink-500" /> Instagram / Social Handle
            </label>
            <GlassInput
              value={form.instagramHandle}
              onChange={e => update('instagramHandle', e.target.value)}
              placeholder="@username"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-1 text-sm">
                <FaCalendar className="inline mr-1 text-slate-400" /> Wedding Anniversary
              </label>
              <GlassInput
                type="date"
                value={form.anniversaryDate}
                onChange={e => update('anniversaryDate', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-1 text-sm">
                <FaCalendar className="inline mr-1 text-slate-400" /> Date of Birth
              </label>
              <GlassInput
                type="date"
                value={form.dateOfBirth}
                onChange={e => update('dateOfBirth', e.target.value)}
              />
            </div>
          </div>

          {/* Channel Source */}
          {!urlSource && (
            <div>
              <label className="block text-slate-700 font-semibold mb-1 text-sm">
                <FaLink className="inline mr-1 text-slate-400" /> Channel Source
              </label>
              <GlassSelect
                value={form.channelSource}
                onChange={e => update('channelSource', e.target.value)}
              >
                {CHANNEL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </GlassSelect>
            </div>
          )}
          {urlSource && (
            <GlassCard className="bg-blue-50 border-blue-200">
              <p className="text-blue-700 text-sm font-medium">
                Source auto-detected: <span className="font-bold">{urlSource}</span>
              </p>
            </GlassCard>
          )}

          {/* Profession */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1 text-sm">Profession</label>
            <GlassSelect
              value={form.profession}
              onChange={e => update('profession', e.target.value)}
            >
              {PROFESSION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </GlassSelect>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-slate-700 font-semibold mb-1 text-sm">Reference Name & Phone (Optional)</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <GlassInput
                value={form.referenceName}
                onChange={e => update('referenceName', e.target.value)}
                placeholder="Referred by"
              />
              <GlassInput
                type="tel"
                value={form.referencePhone}
                onChange={e => update('referencePhone', e.target.value)}
                placeholder="Reference phone"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex flex-col md:flex-row gap-3 mt-8">
          <GlassButton
            variant="primary"
            className="flex-1"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Customer'}
          </GlassButton>
          <GlassButton
            variant="secondary"
            className="flex-1"
            onClick={() => onNavigate?.('dashboard')}
          >
            Cancel
          </GlassButton>
        </div>
      </GlassPanel>
    </div>
  );
};

export default CustomerOnboardingPage;
